import styled from "@emotion/styled"
import { Dialog } from "@mui/material"

const StyledRole = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 20px 22px 24px;
  border-radius: 24px;
  background: radial-gradient(
      circle at 4% -8%,
      rgba(132, 70, 173, 0.16) 0%,
      transparent 35%
    ),
    radial-gradient(
      circle at 98% 108%,
      rgba(201, 159, 253, 0.22) 0%,
      transparent 42%
    ),
    var(--cms-content-bg);

  .heading-copy {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .page-title {
    margin: 0;
    font-size: 30px;
    font-weight: 800;
    color: var(--cms-text-primary);
    letter-spacing: -0.01em;
  }

  .page-subtitle {
    margin: 0;
    color: var(--cms-text-secondary);
    font-size: 13px;
    font-weight: 500;
  }

  .new-role-btn {
    text-transform: none;
    font-weight: 700;
    border-radius: 10px;
    min-height: 40px;
    padding: 8px 16px;
    box-shadow: 0 12px 24px rgba(132, 70, 173, 0.28);
  }

  .back-btn {
    text-transform: none;
    border-radius: 10px;
    min-height: 40px;
    font-weight: 700;
    padding: 8px 14px;
  }

  .page-surface {
    border: 1px solid var(--cms-border);
    border-radius: 18px;
    background: var(--cms-surface-secondary);
    box-shadow: 0 14px 30px rgba(15, 23, 42, 0.08);
    padding: 14px;
  }

  .page-empty {
    border: 1px dashed var(--cms-border);
    border-radius: 12px;
    background: var(--cms-surface-muted);
    padding: 26px 16px;
    text-align: center;
    color: var(--cms-text-secondary);
    font-weight: 600;
  }
`

const StyledRoleTable = styled.div`
  display: flex;
  flex-direction: column;

  .table-shell .MuiTableContainer-root {
    border: 1px solid var(--cms-border);
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 12px 24px rgba(15, 23, 42, 0.06);
  }

  .table-shell .MuiTableCell-head {
    background: var(--cms-table-head-bg);
    color: var(--cms-text-primary);
    font-weight: 700;
    font-size: 12px;
    border-bottom: 1px solid var(--cms-border);
  }

  .table-shell .MuiTableCell-body {
    font-size: 13px;
    color: var(--cms-text-primary);
    border-bottom: 1px solid var(--cms-border);
    vertical-align: top;
  }

  .table-empty {
    text-align: center;
    color: var(--cms-text-secondary);
    font-weight: 600;
    padding: 18px;
  }

  .row-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    flex-wrap: wrap;
  }

  .row-actions .MuiButton-root {
    text-transform: none;
    border-radius: 9px;
    min-height: 34px;
    font-weight: 700;
    padding: 4px 10px;
  }

  .pagination-section {
    display: flex;
    justify-content: flex-end;
    padding-top: 8px;
  }
`

const StyledRoleDialog = styled(Dialog)`
  .MuiPaper-root {
    width: min(720px, calc(100vw - 28px));
    max-width: none;
    border-radius: 18px;
    border: 1px solid var(--cms-border);
    background: var(--cms-dialog-bg);
    box-shadow: 0 24px 48px rgba(15, 23, 42, 0.2);
  }

  .MuiDialogTitle-root {
    padding: 18px 22px 8px;
  }

  .MuiDialogContent-root {
    padding: 10px 22px 10px;
  }

  .MuiDialogActions-root {
    padding: 8px 22px 18px;
    gap: 10px;
  }

  .dialog-title-group {
    display: flex;
    flex-direction: column;
    gap: 5px;
  }

  .dialog-title {
    margin: 0;
    font-size: 20px;
    font-weight: 800;
    color: var(--cms-text-primary);
    letter-spacing: -0.01em;
  }

  .dialog-subtitle {
    margin: 0;
    font-size: 13px;
    color: var(--cms-text-secondary);
    font-weight: 500;
  }

  .dialog-cancel-btn,
  .dialog-submit-btn {
    text-transform: none;
    border-radius: 10px;
    min-height: 40px;
    font-weight: 700;
    padding: 8px 14px;
  }

  .dialog-submit-btn {
    box-shadow: 0 12px 24px rgba(132, 70, 173, 0.28);
  }

  .error {
    font-size: 0.75rem;
    color: #d32f2f;
    margin-top: 3px;
  }
`

const StyledRoleDetail = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;

  .role-summary-card,
  .permission-card {
    border: 1px solid var(--cms-border);
    border-radius: 12px;
    box-shadow: none;
    background: var(--cms-surface-secondary);
  }

  .summary-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
  }

  .summary-item {
    border: 1px solid var(--cms-border);
    border-radius: 10px;
    background: var(--cms-surface-muted);
    padding: 10px 12px;
  }

  .summary-label {
    font-size: 12px;
    font-weight: 700;
    color: var(--cms-text-secondary);
    text-transform: uppercase;
    letter-spacing: 0.04em;
    margin-bottom: 5px;
  }

  .summary-value {
    font-size: 14px;
    color: var(--cms-text-primary);
    font-weight: 600;
    word-break: break-word;
  }

  .permission-header {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    margin-bottom: 4px;
  }

  .permission-title {
    margin: 0;
    font-size: 20px;
    color: var(--cms-text-primary);
    font-weight: 800;
    letter-spacing: -0.01em;
  }

  .permission-header .MuiButton-root {
    text-transform: none;
    border-radius: 9px;
    min-height: 36px;
    font-weight: 700;
    padding: 5px 12px;
  }

  .module-section {
    border: 1px solid var(--cms-border);
    border-radius: 10px;
    background: var(--cms-surface-muted);
    padding: 10px 12px;
    margin-top: 10px;
  }

  .module-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
  }

  .module-title {
    font-size: 14px;
    font-weight: 700;
    color: var(--cms-text-primary);
    margin: 0;
  }

  .select-all-checkbox {
    margin-left: 0;
  }

  .permission-list {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 4px 10px;
  }

  .MuiFormControlLabel-label {
    font-size: 13px;
    color: var(--cms-text-primary);
  }

  .detail-loading {
    border: 1px dashed var(--cms-border);
    border-radius: 12px;
    background: var(--cms-surface-muted);
    padding: 26px 16px;
    text-align: center;
    color: var(--cms-text-secondary);
    font-weight: 600;
  }

  @media (max-width: 840px) {
    .summary-grid {
      grid-template-columns: minmax(0, 1fr);
    }
  }
`

export { StyledRole, StyledRoleTable, StyledRoleDialog, StyledRoleDetail }
